import { DropdownMenu as Menu } from 'radix-ui'
import { Check } from 'lucide-react'
import type { ComponentPropsWithoutRef, ReactNode } from 'react'
import { cn } from '@/utils/cn'

export const DropdownMenu = Menu.Root
export const DropdownMenuTrigger = Menu.Trigger

export function DropdownMenuContent({
  className,
  align = 'end',
  children,
  ...props
}: ComponentPropsWithoutRef<typeof Menu.Content>) {
  return (
    <Menu.Portal>
      <Menu.Content
        align={align}
        sideOffset={6}
        className={cn(
          'z-50 min-w-48 animate-scale-in overflow-hidden rounded-control border border-line bg-surface p-1 shadow-overlay',
          className,
        )}
        {...props}
      >
        {children}
      </Menu.Content>
    </Menu.Portal>
  )
}

interface ItemProps extends ComponentPropsWithoutRef<typeof Menu.Item> {
  icon?: ReactNode
  tone?: 'default' | 'danger'
  shortcut?: string
}

export function DropdownMenuItem({
  className,
  icon,
  tone = 'default',
  shortcut,
  children,
  ...props
}: ItemProps) {
  return (
    <Menu.Item
      className={cn(
        'flex cursor-pointer items-center gap-2.5 rounded-[0.45rem] px-2.5 py-2 text-sm outline-none select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:size-4',
        tone === 'danger'
          ? 'text-danger-600 data-[highlighted]:bg-danger-50'
          : 'text-ink-soft data-[highlighted]:bg-surface-sunken data-[highlighted]:text-ink',
        className,
      )}
      {...props}
    >
      {icon ? (
        <span className={cn(tone === 'danger' ? 'text-danger-500' : 'text-subtle')}>{icon}</span>
      ) : null}
      <span className="flex-1">{children}</span>
      {shortcut ? <kbd className="text-2xs text-subtle">{shortcut}</kbd> : null}
    </Menu.Item>
  )
}

export function DropdownMenuLabel({ children }: { children: ReactNode }) {
  return (
    <Menu.Label className="px-2.5 pt-2 pb-1 text-2xs font-semibold tracking-wide text-subtle uppercase">
      {children}
    </Menu.Label>
  )
}

export function DropdownMenuSeparator() {
  return <Menu.Separator className="my-1 h-px bg-line" />
}

export const DropdownMenuRadioGroup = Menu.RadioGroup

/** Checkable menu option (single choice within a DropdownMenuRadioGroup). */
export function DropdownMenuRadioItem({
  className,
  children,
  icon,
  ...props
}: ComponentPropsWithoutRef<typeof Menu.RadioItem> & { icon?: ReactNode }) {
  return (
    <Menu.RadioItem
      className={cn(
        'flex cursor-pointer items-center gap-2.5 rounded-[0.45rem] px-2.5 py-2 text-sm text-ink-soft outline-none select-none data-[highlighted]:bg-surface-sunken data-[highlighted]:text-ink data-[state=checked]:font-medium data-[state=checked]:text-brand-700 [&_svg]:size-4',
        className,
      )}
      {...props}
    >
      {icon ? <span className="text-subtle">{icon}</span> : null}
      <span className="flex-1">{children}</span>
      <Menu.ItemIndicator>
        <Check className="size-4 text-brand-600" aria-hidden="true" />
      </Menu.ItemIndicator>
    </Menu.RadioItem>
  )
}
